/**
 * UAPC Anytime - Favorites Page
 * @module FavoritesPage
 * @author Roohi Fatima - Frontend Developer
 * @description User's favorite parts
 * @version 1.0.0
 * Git: Roohi Fatima <roohi@linesoft.com> - Favorites feature
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShoppingCart, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import Layout from '../components/Layout';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FavoritesPage = () => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/favorites`);
      setFavorites(response.data.parts || []);
    } catch (error) {
      toast.error('Failed to load favorites');
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (partId) => {
    try {
      await axios.delete(`${API_URL}/api/favorites/${partId}`);
      setFavorites(favorites.filter(p => p.id !== partId));
      toast.success('Removed from favorites');
    } catch (error) {
      toast.error('Failed to remove from favorites');
    }
  };

  const handleAddToCart = async (partId) => {
    await addToCart(partId);
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-10 bg-slate-200 dark:bg-slate-800 w-48" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-64 bg-slate-200 dark:bg-slate-800" />
              ))}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="favorites-page">
        <h1 className="font-heading font-black text-4xl text-slate-900 dark:text-white uppercase tracking-tight mb-8">
          My Favorites
        </h1>

        {favorites.length === 0 ? (
          <div className="text-center py-16">
            <Heart className="w-20 h-20 text-slate-300 mx-auto mb-6" />
            <h2 className="font-heading font-bold text-2xl text-slate-900 dark:text-white uppercase mb-4">
              No Favorites Yet
            </h2>
            <p className="text-slate-600 dark:text-slate-400 mb-8">
              Start browsing and save parts you're interested in.
            </p>
            <Link to="/search">
              <Button className="btn-industrial">
                Browse Parts
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {favorites.map((part, index) => (
              <div
                key={part.id}
                className="group bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 hover:border-blue-500 transition-colors"
                data-testid={`favorite-${index}`}
              >
                <Link to={`/parts/${part.id}`}>
                  <div className="aspect-square bg-slate-100 dark:bg-slate-800 mb-4 flex items-center justify-center overflow-hidden">
                    {part.image_url ? (
                      <img src={part.image_url} alt={part.name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-slate-400 font-mono text-sm">{part.part_number}</span>
                    )}
                  </div>
                </Link>
                <div className="font-mono text-xs text-slate-500 uppercase tracking-wider mb-1">
                  {part.part_number}
                </div>
                <Link to={`/parts/${part.id}`}>
                  <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white line-clamp-2 hover:text-blue-600">
                    {part.name}
                  </h3>
                </Link>
                <div className="mt-2 flex items-center justify-between">
                  <span className="font-heading font-bold text-xl text-blue-600">
                    ${part.price.toFixed(2)}
                  </span>
                  <span className={`px-2 py-1 text-xs font-bold uppercase ${
                    part.condition === 'new' ? 'bg-green-100 text-green-800' :
                    part.condition === 'refurbished' ? 'bg-blue-100 text-blue-800' :
                    'bg-slate-100 text-slate-800'
                  }`}>
                    {part.condition}
                  </span>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button 
                    onClick={() => handleAddToCart(part.id)}
                    className="flex-1 bg-orange-500 hover:bg-orange-600 text-sm"
                    data-testid={`add-to-cart-${index}`}
                  >
                    <ShoppingCart className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => removeFavorite(part.id)}
                    className="text-red-600 hover:text-red-700"
                    data-testid={`remove-favorite-${index}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default FavoritesPage;
